import './styles.scss'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from '../../../hooks/hooks'
import { Input } from '../../../components/Input'
import { BTN_TXT, PLACEHOLDERS } from '../../../constants'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { setLoading } from '../../../redux/store/common/slice'
import { GPOItem, GpoRequestData } from './types'
import { getGpoList } from './api'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { getUser } from '../../../redux/store/user/getters'
import { ROLES } from '../../Permission'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { routes } from '../../../router'
import { useNavigate } from 'react-router'
import { TQueryParams } from '../../../components/Table/types'
import { Table } from '../../../components/Table'
import { columns } from './constants'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import axios, { CancelTokenSource } from 'axios'

export const Gpo = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(getUser)

  const [firstLoad, setFirstLoad] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [totalNumber, setTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [gpoData, setGpoData] = useState<GPOItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [sortParams, setSortParams] = useState<GpoRequestData>()

  const isCommonsPresident = user.role === ROLES.COMMONS_VICE_PRESIDENT
  const debouncedSearch = useDebounce(searchValue, 500)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  useEffect(() => {
    return () => {
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        void getTableData({ ...sortParams, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const getTableData = async ({
    sortField,
    sortOrder,
    page
  }: GpoRequestData) => {
    try {
      dispatch(setLoading(true))
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: TQueryParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ?? pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      const { data } = await getGpoList({
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
      if (!data?.results) {
        return
      }
      setGpoData(data.results)
      setTotalNumber(data.count)
      if (firstLoad) {
        setFirstLoad(false)
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    void getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    void getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys)
  }

  const handleRedirect = () => navigate(routes.gpo + '/create')
  const handleEdit = () => navigate(routes.gpo + '/' + selectedRowKeys[0])

  return (
    <div className="gpo-container">
      <div className="row space-between">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: PLACEHOLDERS.SEARCH_BY_GPO_NAME,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
        <div className="row">
          {isCommonsPresident && !selectedRowKeys?.length && (
            <Button
              className="contract-categories__add-btn"
              onClick={handleRedirect}
              icon={<Plus16 />}
              type={BUTTON_TYPES.PRIMARY}
              upperCase
            >
              {BTN_TXT.ADD_GPO}
            </Button>
          )}
          {isCommonsPresident && selectedRowKeys?.length === 1 && (
            <Button
              className="contract-categories__add-btn"
              onClick={handleEdit}
              icon={<Edit16 />}
              type={BUTTON_TYPES.PRIMARY}
              upperCase
            >
              {BTN_TXT.EDIT}
            </Button>
          )}
        </div>
      </div>
      <Table<GPOItem>
        dataSource={gpoData}
        onChange={handleTableChange}
        className="hospitals-list-table mt-4"
        columns={columns}
        rowKey={(row) => row?.uuid}
        onChangePage={handleChangePageSize}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        createButton={{
          buttonText: BTN_TXT.ADD_GPO,
          availability: isCommonsPresident,
          action: handleRedirect
        }}
        rowSelection={
          isCommonsPresident
            ? {
                onChange: handleSelectRow,
                selectedRowKeys
              }
            : undefined
        }
      />
    </div>
  )
}
